import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import useAuth from "../../../hooks/useAuth";
import { actsAsAgencyAdmin } from "../../../utils/permissions";
import confirmationService from "../../../services/confirmation.service";
import { formatVehicleNumberDisplay } from "../../../utils/vehicleNumberUtils";
import { withReturnPath } from "../../../utils/navReturn";
import PaginationBar from "../../../components/common/PaginationBar";
import ExportExcelButton from "../../../components/common/ExportExcelButton";
import { saveExcelBlob, getApiErrorMessage } from "../../../utils/downloadExcel";
import { PAGE_LIMIT } from "../../../constants/pagination";
import { readListItems, readPagination } from "../../../utils/listPagination";
import "../../../styles/bankDetails.css";
import "../../../styles/users.css";
import "../../../styles/confirmation.css";

const PAGE_SIZE = PAGE_LIMIT;

function statusBadge(status) {
  const normalized = String(status || "").toUpperCase();
  if (normalized === "PENDING") return "cf-badge cf-badge--pending";
  if (normalized === "CONFIRMED") return "cf-badge cf-badge--confirmed";
  if (normalized === "REJECTED") return "cf-badge cf-badge--rejected";
  return "cf-badge";
}

function formatReportedAt(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return "—";
  }
}

function sortCasesNewestFirst(rows) {
  return [...rows].sort((a, b) => {
    const diff =
      new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
    if (diff !== 0) return diff;
    return String(b?._id || "").localeCompare(String(a?._id || ""));
  });
}

export default function CasesList() {
  const { auth } = useAuth();
  const navigate = useNavigate();
  const [allRows, setAllRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [exporting, setExporting] = useState(false);
  const [appliedQuery, setAppliedQuery] = useState("");

  useEffect(() => {
    setPage(1);
  }, [appliedQuery]);

  const pageRows = allRows;

  const load = useCallback(async () => {
    if (!auth?.token) return;
    setLoading(true);
    setError("");
    try {
      const res = await confirmationService.getAll(auth.token, {
        status: "PENDING,CONFIRMED,REJECTED",
        page,
        limit: PAGE_LIMIT,
        q: appliedQuery.trim() || undefined,
      });
      const items = readListItems(res);
      setAllRows(sortCasesNewestFirst(items));
      setPagination(readPagination(res));
    } catch (err) {
      setError(err?.response?.data?.message || "Could not load cases.");
      setAllRows([]);
    } finally {
      setLoading(false);
    }
  }, [auth?.token, page, appliedQuery]);

  useEffect(() => {
    load();
  }, [load]);

  const onDownloadExcel = async () => {
    if (!auth?.token) return;
    setExporting(true);
    setError("");
    try {
      const params = {
        status: "PENDING,CONFIRMED,REJECTED",
      };
      if (appliedQuery.trim()) params.q = appliedQuery.trim();
      const response = await confirmationService.downloadExcel(
        auth.token,
        params
      );
      await saveExcelBlob(response, "trace-cases.xlsx");
    } catch (err) {
      setError(await getApiErrorMessage(err, "Failed to download Excel"));
    } finally {
      setExporting(false);
    }
  };

  const openCase = (row) => {
    if (!row?.caseId) return;
    navigate(withReturnPath(`/details-view?id=${row.caseId}`, "/cases"), {
      state: { from: "/cases" },
    });
  };

  if (!actsAsAgencyAdmin(auth)) {
    return <Navigate to="/find-vehicles" replace />;
  }

  return (
    <div className="bd-page cases-page cf-page">
      <div className="users-actions">
        <h2>All Trace Vehicle Details</h2>
      </div>
      <form
        className="cf-list-search cases-page__search"
        onSubmit={(e) => {
          e.preventDefault();
          setPage(1);
          setAppliedQuery(search.trim());
        }}
      >
        <label className="cf-list-search__field">
          <span className="sr-only">Search trace cases</span>
          <input
            type="text"
            placeholder="Vehicle, customer, loan account, bank / NBFC…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoComplete="off"
          />
        </label>
        <button type="submit" className="primary-page-btn cf-list-search__btn">
          Search
        </button>
        <button
          type="button"
          className="secondary-page-btn cf-list-search__btn"
          onClick={() => {
            setSearch("");
            setAppliedQuery("");
            setPage(1);
          }}
        >
          Reset
        </button>
        <ExportExcelButton onClick={onDownloadExcel} loading={exporting} />
      </form>

      {error && <p className="bd-feedback bd-error">{error}</p>}

      {loading ? (
        <p className="cf-excel-loading">Loading cases…</p>
      ) : pageRows.length === 0 && !error ? (
        <p className="cf-excel-loading">
          {appliedQuery.trim()
            ? `No cases match “${appliedQuery.trim()}”.`
            : "No pending, confirmed, or rejected cases yet. When field staff trace a vehicle, it will appear here."}
        </p>
      ) : (
        <div className="company-table-wrap cf-excel-sheet cases-page__table table-scroll">
          <table className="users-table excel-grid-table cf-excel-list-table">
            <thead>
              <tr>
                <th>S.No.</th>
                <th>Registration Number</th>
                <th>Customer Name</th>
                <th>Bank / NBFC Name</th>
                <th>Branch Name</th>
                <th>Status</th>
                <th>User</th>
                <th>Role</th>
                <th>Reported At</th>
                <th className="cf-action-th">Tracer report</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((row, index) => {
                const status = String(row.status || "—").toUpperCase();

                return (
                  <tr
                    key={row._id}
                    className={`${status === "PENDING" ? "confirmation-row-pending" : ""} confirmation-row-clickable`}
                    onClick={() => openCase(row)}
                  >
                    <td>
                      {Math.max(
                        Number(pagination.total || pageRows.length) -
                          ((pagination.page || page) - 1) * PAGE_SIZE -
                          index,
                        1
                      )}
                    </td>
                    <td>{formatVehicleNumberDisplay(row.vehicleNumber) || "—"}</td>
                    <td>{row.customerName || "—"}</td>
                    <td>{row.bankName || "—"}</td>
                    <td>{row.branchName || "—"}</td>
                    <td className="cases-status-cell">
                      <span className={`${statusBadge(status)} cases-status-badge`}>
                        {status}
                      </span>
                    </td>
                    <td>{row.requestedByName || "—"}</td>
                    <td>{row.requestedByRoleLabel || row.requestedByRole || "—"}</td>
                    <td>{formatReportedAt(row.createdAt)}</td>
                    <td className="cf-action-td" onClick={(e) => e.stopPropagation()}>
                      {row.caseId ? (
                        <div className="bd-case-actions" style={{ minWidth: 0 }}>
                          <Link
                            to={withReturnPath(`/details-view?id=${row.caseId}`, "/cases")}
                            className="bd-btn bd-btn-primary bd-btn-compact"
                            state={{ from: "/cases" }}
                          >
                            Details
                          </Link>
                        </div>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!loading && (
        <PaginationBar
          page={pagination.page || page}
          totalPages={pagination.totalPages}
          total={pagination.total}
          loading={loading}
          onPageChange={setPage}
        />
      )}
    </div>
  );
}
